import type { CrudChildGrid, CrudField, ModuleCrudConfig } from "./moduleCrud";

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC ▸ Internal Audit — the four G5 forms (Complaint Initiation, Complaint Correction Verification,
// Nonconformity Initiation, Nonconformity Correction Verification) plus their History lists and the two child
// grids (Corrective Action Log, Corrective Action Verification). The Client Complaint and Nonconformity
// kinds are identical in shape, so both are produced by ONE factory. Behaviour reference:
// docs/03-Code-Change-Documentation/Migration-Log/InternalAudit-G5-Spec.md.
// ─────────────────────────────────────────────────────────────────────────────

interface AuditKind {
  /** Short id used in module keys: qa-<id>-init / -verif / -hist / -log / -ver. */
  id: "cc" | "nca";
  /** Initiation API (api/v1/…). */
  base: string;
  logBase: string;
  verifBase: string;
  /** Audit form name = the G5 nav caption of the initiation form. */
  formName: string;
  /** Singular noun for messages ("complaint" / "nonconformity"). */
  noun: string;
  /** G5 caption of the id field and of the section holding the incident text. */
  idLabel: string;
  sectionLabel: string;
  /** G5 captions of the two "days remaining" fields (the NCA form captions both just "Days Remaining"). */
  investigateDaysLabel: string;
  correctiveDaysLabel: string;
  /** The id caption on the INITIATION detail only (G5 captions the NCA id "NCA ID" there and "NCAID" on Verification / History). */
  idLabelInitiation: string;
  /** Title of the Verification grid (G5: the NCA group is plural, the complaint one singular). */
  verificationTitle: string;
}

const KINDS: AuditKind[] = [
  {
    id: "cc", base: "/api/v1/client-complaints", logBase: "/api/v1/client-complaint-logs",
    verifBase: "/api/v1/client-complaint-verifications", formName: "Complaint Initiation",
    noun: "complaint", idLabel: "CCID", sectionLabel: "Complaint", idLabelInitiation: "CCID", verificationTitle: "Corrective Action Verification",
    investigateDaysLabel: "Investigate Days Remaining", correctiveDaysLabel: "Corrective Days Remaining",
  },
  {
    id: "nca", base: "/api/v1/nonconformities", logBase: "/api/v1/nonconformity-logs",
    verifBase: "/api/v1/nonconformity-verifications", formName: "Nonconformity Initiation",
    noun: "nonconformity", idLabel: "NCAID", sectionLabel: "Nonconformity", idLabelInitiation: "NCA ID", verificationTitle: "Corrective Action Verifications",
    investigateDaysLabel: "Days Remaining", correctiveDaysLabel: "Days Remaining",
  },
];

/** The detail fields in G5's layout: group "Incident Information" (two columns), then the group named after the kind
 *  ("Complaint" / "Nonconformity": the incident text beside the four lookups). Row-major, so each left/right pair below is one row. */
function initiationFields(k: AuditKind, readOnly: boolean, view: "init" | "verif" | "hist"): CrudField[] {
  const ro = readOnly ? { readOnly: true } : {};
  return [
    { key: "__sec_incident", label: "Incident Information", type: "section" },
    { key: "recordId", label: view === "init" ? k.idLabelInitiation : k.idLabel, type: "readonly", placeholder: "AutoGenerate" },
    { key: "investigateDueDate", label: "Investigate Due Date", type: "date", ...ro },
    { key: "dateInitiated", label: "Date Initiated", type: "date", ...ro },
    { key: "investigateDaysRemaining", label: k.investigateDaysLabel, type: "readonly", daysUntilKey: "investigateDueDate" },
    { key: "initiatorName", label: "Initiator", type: "readonly", defaultCurrentUserName: true },
    { key: "correctiveActionDueDate", label: "Corrective Action Due Date", type: "date", ...ro },
    { key: "subject", label: "Subject", type: "text", maxLength: 100, ...ro },
    { key: "correctiveDaysRemaining", label: k.correctiveDaysLabel, type: "readonly", daysUntilKey: "correctiveActionDueDate" },
    { key: "assignedTo", label: "Assigned To", type: "select", aiCombo: true, erasable: true, storedLabelKey: "assignedToName", optionsUrl: `${k.base}/employees`, noneLabel: "N/A", ...ro },
    { key: "statusName", label: "Status", type: "readonly", defaultValue: "Pending submission" },
    { key: "responsible", label: "Responsible", type: "multiselect", summarySeparator: "; ", optionsUrl: `${k.base}/responsible-employees`, noneLabel: "Select…", ...ro },
    { key: "_gap", label: "", type: "spacer" },
    { key: "__sec_kind", label: k.sectionLabel, type: "section" },
    { key: "incidentSpecifics", label: view === "hist" ? "Incident Specifics" : "Incident Specific", type: "textarea", textRows: 7, rowSpan: 4, ...ro },
    { key: "affectedDepartment", label: "Affected Department", type: "multiselect", summarySeparator: "; ", optionsUrl: `${k.base}/departments`, noneLabel: "Select…", ...ro },
    { key: "affectedClients", label: view === "hist" ? "Affected Clients" : "Affected Client", type: "select", aiCombo: true, erasable: true, storedLabelKey: "affectedClientsName", optionsUrl: `${k.base}/clients`, noneLabel: "N/A", ...ro },
    {
      key: "problemCategory", label: "Problem Category", type: "select", erasable: true, optionsUrl: `${k.base}/problem-categories`, noneLabel: "N/A", ...ro,
      // G5 shows New beside the caret on this lookup (the Problem Category master has no other rule), so a new one is added inline.
      quickAdd: {
        createUrl: "/api/v1/problem-categories", title: "Problem Category", icon: "ti-category", nameKey: "category", welded: true,
        fields: [{ key: "category", label: "Problem Category", type: "text", required: true, maxLength: 200, requiredMessage: "'Problem Category' must not be empty." }],
      },
    },
    { key: "reasonDiscovered", label: "Reason Discovered", type: "multiselect", summarySeparator: "; ", optionsUrl: `${k.base}/reasons`, noneLabel: "Select…", ...ro },
  ];
}

/** The Corrective Action Log grid. G5 has TWO list views for it: the initiation form's own (Complaint: Date · Employee ·
 *  Attachment · CorrectiveAction; Nonconformity: Date · Employee · Description · CorrectiveAction · Attachment) and the
 *  "pending verification" one shown on the Verification / History forms (Date · Employee · Description · Action · Download). */
function logGrid(k: AuditKind, editable: boolean, onInitiation: boolean): CrudChildGrid {
  const file = (label: string) => ({
    key: "attachmentFileName", label, width: 170, type: "file" as const,
    downloadUrlBase: `${k.logBase}/file`, fileOidKey: "attachment",
  });
  const columns: CrudChildGrid["columns"] = !onInitiation
    ? [
        { key: "date", label: "Date", width: 150, type: "datetime" },
        { key: "employeeName", label: "Employee", width: 150 },
        { key: "description", label: "Description", width: 260 },
        { key: "correctiveAction", label: "Action", width: 380 },
        file("Download"),
      ]
    : k.id === "cc"
      ? [
          { key: "date", label: "Date", width: 150, type: "datetime" },
          { key: "employeeName", label: "Employee", width: 150 },
          file("Attachment"),
          { key: "correctiveAction", label: "CorrectiveAction", width: 380 },
        ]
      : [
          { key: "date", label: "Date", width: 150, type: "datetime" },
          { key: "employeeName", label: "Employee", width: 150 },
          { key: "description", label: "Description", width: 260 },
          { key: "correctiveAction", label: "CorrectiveAction", width: 380 },
          file("Attachment"),
        ];
  return {
    title: "Corrective Action Log",
    listUrl: `${k.logBase}/by-parent/{id}`,
    childKey: `qa-${k.id}-log`,
    parentField: "parent",
    listReturnsFullRecord: true,
    exportButton: true,
    multiSelect: true,
    rowEditIcon: editable,
    hideNew: !editable,
    hideEdit: !editable,
    hideDelete: !editable,
    coloredIcons: true,
    columns,
  };
}

/** G5 shows the Corrective Action Verification as Date · ActionCategory · Text · Author · Position · Attachment.
 *  Hidden unless the user may close (legacy hides the whole grid for anyone else). */
function verificationGrid(k: AuditKind, editable: boolean, gatedByClosePermission: boolean): CrudChildGrid {
  return {
    title: k.verificationTitle,
    listUrl: `${k.verifBase}/by-parent/{id}`,
    childKey: `qa-${k.id}-ver`,
    parentField: "parent",
    listReturnsFullRecord: true,
    exportButton: true,
    multiSelect: true,
    rowEditIcon: editable,
    hideNew: !editable,
    hideEdit: !editable,
    hideDelete: !editable,
    actionsInView: true,
    refreshButton: true,
    coloredIcons: true,
    // G5 hides the group only on the Verification detail (the History detail always shows it).
    ...(gatedByClosePermission ? { visibleWhen: (v: Record<string, unknown>) => v.canClose === true } : {}),
    columns: [
      { key: "date", label: "Date", width: 150, type: "datetime" },
      { key: "actionCategoryName", label: "ActionCategory", width: 160 },
      { key: "text", label: "Text", width: 360 },
      { key: "authorName", label: "Author", width: 150 },
      { key: "positionName", label: "Position", width: 140 },
      {
        key: "attachmentFileName", label: "Attachment", width: 170, type: "file",
        downloadUrlBase: `${k.verifBase}/file`, fileOidKey: "attachment",
      },
    ],
  };
}

const COMMON_DETAIL = {
  idField: "oid",
  sideLabels: true,
  // "Corrective Action Due Date:" / "Corrective Days Remaining:" must fit their label column, not be cut with "…".
  horizontalLabelWidth: 190,
  squareControls: true,
  controlHeight24: true,
  // status / canClose are not form fields, but the Submit / Close / Verification-grid rules read them.
  passthroughKeys: ["status", "canClose", "assignedToName", "affectedClientsName"],
} satisfies Partial<ModuleCrudConfig>;

function build(k: AuditKind): Record<string, ModuleCrudConfig> {
  const initKey = `qa-${k.id}-init`;
  const verifKey = `qa-${k.id}-verif`;
  const histKey = `qa-${k.id}-hist`;

  return {
    // ── Initiation: New · Edit · Delete · Refresh · Submit · Export to; Corrective Action Log grid below. ──
    [initKey]: {
      ...COMMON_DETAIL,
      basePath: k.base,
      formName: k.formName,
      entityLabel: k.noun[0]!.toUpperCase() + k.noun.slice(1),
      fields: initiationFields(k, false, "init"),
      // G5 initiation bar: New · Delete · Refresh · Submit.
      // G5: double-clicking a row opens it READ-ONLY (mode=View); the row pencil opens it for edit.
      rowDoubleClickView: true,
      detailActionsSplit: true,
      hideSectionHeader: true,
      detailShowNew: true,
      detailShowDelete: true,
      detailShowRefresh: true,
      detailRecordActionsLast: true,
      detailBarOnNew: true,
      saveSuccessMessage: "Saved successfully.",
      childGrids: [logGrid(k, true, true)],
      listActions: [{
        key: "submit", label: "Submit", icon: "⬆", path: "submit",
        confirmTitle: "Submit", confirmMessage: `Submit the selected ${k.noun} records for verification?`,
        successMessage: "Submitted successfully.", placement: "beforeExport",
      }],
      recordActions: [{
        key: "submit", label: "Submit", icon: "⬆", path: "submit", primary: true, saveFirst: true, closeAfter: true,
        confirmTitle: "Submit", confirmMessage: `Submit this ${k.noun} for verification?`,
        successMessage: "Submitted successfully.",
        disabledOnNew: true,
        visibleWhen: (v) => v.status === 0 || v.status === undefined,
      }],
    },

    // ── Correction Verification: header read-only; Close (permission-gated) + the Verification grid. ──
    [verifKey]: {
      ...COMMON_DETAIL,
      basePath: k.base,
      formName: k.formName,
      entityLabel: k.noun[0]!.toUpperCase() + k.noun.slice(1),
      hideNewButton: true,
      detailHideSaveNew: true,
      // G5 verification bar: Delete · Refresh · Close; its Save commits nothing (every field is read-only).
      // G5: double-clicking a row opens it READ-ONLY (mode=View); the row pencil opens it for edit.
      rowDoubleClickView: true,
      detailActionsSplit: true,
      hideSectionHeader: true,
      detailShowDelete: true,
      detailShowRefresh: true,
      detailRecordActionsLast: true,
      detailSaveNoop: true,
      saveSuccessMessage: "Saved successfully.",
      fields: initiationFields(k, true, "verif"),
      childGrids: [logGrid(k, false, false), verificationGrid(k, true, true)],
      recordActions: [{
        key: "close", label: "Close", icon: "✔", path: "close", primary: true, closeAfter: true,
        confirmTitle: "Close", confirmMessage: `Close this ${k.noun}? It moves to History.`,
        successMessage: "Closed successfully.",
        visibleWhen: (v) => v.canClose === true && v.status === 1,
      }],
    },

    // ── History: closed records, everything read-only. ──
    [histKey]: {
      ...COMMON_DETAIL,
      basePath: k.base,
      formName: k.formName,
      entityLabel: k.noun[0]!.toUpperCase() + k.noun.slice(1),
      hideNewButton: true,
      hideEditButton: true,
      hideDeleteButton: true,
      detailReadOnly: true,
      detailHideEdit: true,
      // G5: double-clicking a row opens it READ-ONLY (mode=View); the row pencil opens it for edit.
      rowDoubleClickView: true,
      detailActionsSplit: true,
      hideSectionHeader: true,
      detailShowRefresh: true,
      fields: initiationFields(k, true, "hist"),
      childGrids: [logGrid(k, false, false), verificationGrid(k, false, false)],
    },

    // ── Child popups (fields only — rows are listed by the parent's child grid). ──
    [`qa-${k.id}-log`]: {
      basePath: k.logBase,
      idField: "oid",
      formName: k.formName,
      entityLabel: "Corrective Action Log",
      dialogTitle: "Corrective Action Log",
      dialogSaveLabel: "OK",
      dialogAccent: true,
      squareControls: true,
      controlHeight24: true,
      fields: [
        { key: "date", label: "Date", type: "date", readOnly: true, defaultNow: true },
        { key: "employeeName", label: "Employee", type: "readonly", defaultCurrentUserName: true },
        { key: "description", label: "Description", type: "textarea", textRows: 4 },
        { key: "correctiveAction", label: "Corrective Action", type: "textarea", textRows: 4 },
        {
          key: "attachment", label: "Attachment", type: "file", fileNameKey: "attachmentFileName",
          uploadUrl: `${k.logBase}/file/upload`, downloadUrlBase: `${k.logBase}/file`,
        },
      ],
    },
    [`qa-${k.id}-ver`]: {
      basePath: k.verifBase,
      idField: "oid",
      formName: k.formName,
      entityLabel: "Corrective Action Verification",
      dialogTitle: "Corrective Action Verification",
      dialogSaveLabel: "OK",
      dialogAccent: true,
      squareControls: true,
      controlHeight24: true,
      fields: [
        { key: "date", label: "Date", type: "date", readOnly: true, defaultNow: true },
        {
          key: "actionCategory", label: "Action Category", type: "select", erasable: true, optionsUrl: `${k.verifBase}/action-categories`, noneLabel: "N/A",
          // G5 shows New beside the caret on this lookup too, so a new Action Category is added inline.
          quickAdd: {
            createUrl: "/api/v1/action-categories", title: "Action Category", icon: "ti-category", nameKey: "category", welded: true,
            fields: [{ key: "category", label: "Action Category", type: "text", required: true, maxLength: 100, requiredMessage: "\"Action Category\" must not be empty." }],
          },
        },
        { key: "text", label: "Text", type: "textarea", textRows: 6 },
        { key: "authorName", label: "Author", type: "readonly", defaultCurrentUserName: true },
        { key: "positionName", label: "Position", type: "readonly", placeholder: "N/A", defaultFromUrl: `${k.base}/my-position` },
        {
          key: "attachment", label: "Attachment", type: "file", fileNameKey: "attachmentFileName",
          uploadUrl: `${k.verifBase}/file/upload`, downloadUrlBase: `${k.verifBase}/file`,
        },
      ],
    },
  };
}

/** Every Internal Audit module config, merged into MODULE_CRUD by moduleCrud.ts. */
export function buildInternalAuditCrud(): Record<string, ModuleCrudConfig> {
  return Object.assign({}, ...KINDS.map(build));
}
